import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { BlockIndent } from '@/lib/richTextIndent';
import { RichTextToolbar } from './RichTextToolbar';

/**
 * Поле "Текст документа" шаблона соглашения — замена Quill::make() из
 * старого Orchid (OperatorEditLayout.php), тот же набор возможностей
 * (форматирование текста, заголовки, картинки, ссылки, цвет текста/фона,
 * цитата, код, списки с отступом) — см. референс-скриншот тулбара Quill.
 * Хранит/отдаёт HTML один в один как раньше отдавал Quill
 * (operator_templates.text — просто longText со сверстанным HTML).
 *
 * Картинка вставляется файлом (не ссылкой на внешний URL) и кодируется в
 * data:-URI прямо внутри HTML — в отличие от Orchid, где Quill грузил файл
 * через Attachment-хранилище и вставлял на него ссылку, здесь такого
 * хранилища у /admin-api нет; data:-URI гарантирует, что картинка реально
 * сохраняется вместе с текстом документа (operator_templates.text), а не
 * ссылкой на файл, который может потеряться.
 *
 * Внешний value — источник истины (как обычный controlled input), поэтому
 * при расхождении с текущим HTML редактора применяем его через
 * setContent(..., {emitUpdate: false}) — иначе каждый setContent от
 * внешнего value порождал бы новый onUpdate/onChange и зацикливал
 * синхронизацию.
 */
export function RichTextEditor({
  value,
  onChange,
  disabled = false,
  ariaLabelledBy,
}: {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
}) {
  const editor = useEditor({
    extensions: [
      // autolink выключён намеренно: форматирование применяется только явным
      // действием пользователя, само по себе при наборе ничего не меняется.
      StarterKit.configure({ link: { openOnClick: false, autolink: false }, heading: { levels: [1, 2] } }),
      // allowBase64 обязателен: по умолчанию у расширения правило разбора
      // 'img[src]:not([src^="data:"])' — вставленная картинка показывалась,
      // но при повторном открытии сохранённого HTML молча выбрасывалась,
      // а мы храним картинки именно data:-URI (см. докблок выше).
      Image.configure({ allowBase64: true }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['paragraph', 'heading'] }),
      BlockIndent,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div className="rich-text-editor">
      <RichTextToolbar editor={editor} disabled={disabled} />
      <EditorContent className="rich-text-editor__content" editor={editor} aria-labelledby={ariaLabelledBy} />
    </div>
  );
}
